import { signOut } from "firebase/auth";

import { auth } from "integrations/firebase/auth";

import { flushNotificationDeletions } from "../notifications/notification-deletion";
import { forgetPushToken } from "../notifications/push-tokens";

/**
 * The sign-out. Use this rather than `signOut(auth)` directly.
 *
 * There are exactly two places a user can sign out — the RequireAuth gate and
 * the /e/$slug route, which deliberately does not mount that gate — and both
 * reach it through useSignOutToLanding, which then goes to "/". Both must
 * surrender this browser's push registration first. A registration token
 * belongs to the browser: leave it behind and the next account to sign in here
 * inherits the device while the previous account's token document still points
 * at it, so notifications for the person who left arrive on the screen of the
 * person who arrived.
 *
 * Cleanup runs first and is awaited, because after `signOut` the user can no
 * longer write their own subtree and the rules would refuse the delete. It is
 * safe to await because `forgetPushToken` bounds itself: it swallows its own
 * failures AND gives up after a few seconds, so the auth call is reached even
 * when the browser is offline and the Firestore delete — which resolves only
 * on server acknowledgement — never settles. Both call sites are
 * fire-and-forget, so an unbounded wait here would be a Sign out button that
 * does nothing, for ever, with no spinner and no error.
 *
 * Only the Firestore half is ordered against `signOut` like this. The FCM
 * surrender authenticates with the Installations header rather than the user's
 * credential, so `forgetPushToken` detaches it and it finishes on its own,
 * before or after the line below.
 *
 * A notification deleted inside its undo window is written first of all. The
 * SDK sends writes in order, so it reaches the server ahead of the token
 * delete that is awaited next, while the user can still write their subtree.
 */
export async function signOutAndForgetDevice(uid: string): Promise<void> {
  flushNotificationDeletions();
  await forgetPushToken(uid);
  await signOut(auth);
}
